import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Sparkle, MagicWand, Translate, TextAlignLeft, FloppyDisk } from "@phosphor-icons/react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function AtelierIA() {
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  useEffect(() => { api.get("/groups").then((r) => setGroups(r.data)).catch(() => {}); }, []);

  // course generation
  const [topic, setTopic] = useState("");
  const [level, setLevel] = useState("débutant");
  const [genLoading, setGenLoading] = useState(false);
  const [course, setCourse] = useState(null);
  const [saving, setSaving] = useState(false);

  const generate = async () => {
    if (!topic.trim()) return toast.error("Indiquez un sujet");
    setGenLoading(true); setCourse(null);
    try {
      const { data } = await api.post("/ai/generate-course", { topic, level, num_lessons: 4 });
      setCourse(data);
      toast.success("Cours généré par l'IA");
    } catch (e) { toast.error(apiError(e)); } finally { setGenLoading(false); }
  };

  const saveCourse = async () => {
    setSaving(true);
    try {
      const { data: c } = await api.post("/courses", {
        title: course.title, description: course.description, category: "IA",
        cover_image: "https://images.pexels.com/photos/31468533/pexels-photo-31468533.jpeg", published: false,
      });
      for (let i = 0; i < (course.lessons || []).length; i++) {
        const l = course.lessons[i];
        await api.post(`/courses/${c.id}/lessons`, { title: l.title, content: l.content, type: "text", order: i + 1 });
      }
      toast.success("Cours enregistré (brouillon)");
      navigate(`/app/cours/${c.id}`);
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  // summarize
  const [sumText, setSumText] = useState("");
  const [summary, setSummary] = useState("");
  const [sumLoading, setSumLoading] = useState(false);
  const summarize = async () => {
    if (!sumText.trim()) return;
    setSumLoading(true); setSummary("");
    try { const { data } = await api.post("/ai/summarize", { text: sumText }); setSummary(data.summary); }
    catch (e) { toast.error(apiError(e)); } finally { setSumLoading(false); }
  };

  // translate
  const [trText, setTrText] = useState("");
  const [target, setTarget] = useState("anglais");
  const [translation, setTranslation] = useState("");
  const [trLoading, setTrLoading] = useState(false);
  const translate = async () => {
    if (!trText.trim()) return;
    setTrLoading(true); setTranslation("");
    try { const { data } = await api.post("/ai/translate", { text: trText, target }); setTranslation(data.translation); }
    catch (e) { toast.error(apiError(e)); } finally { setTrLoading(false); }
  };

  return (
    <div className="space-y-6" data-testid="ai-workshop-page">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-accent">Intelligence artificielle</p>
        <h1 className="flex items-center gap-2 font-display text-3xl font-extrabold tracking-tight">
          <Sparkle size={28} weight="fill" className="text-accent" /> Atelier IA
        </h1>
      </div>

      <Tabs defaultValue="course">
        <TabsList>
          <TabsTrigger value="course" data-testid="tab-gen-course"><MagicWand size={16} className="mr-1" /> Génération de cours</TabsTrigger>
          <TabsTrigger value="summary" data-testid="tab-summary"><TextAlignLeft size={16} className="mr-1" /> Résumés</TabsTrigger>
          <TabsTrigger value="translate" data-testid="tab-translate"><Translate size={16} className="mr-1" /> Traduction</TabsTrigger>
        </TabsList>

        <TabsContent value="course" className="space-y-4">
          <div className="rounded-xl border border-border bg-card p-6">
            <div className="flex flex-wrap items-end gap-3">
              <div className="flex-1 min-w-56"><Label>Sujet du cours</Label><Input value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="Ex: Le marketing digital et les réseaux sociaux" data-testid="gen-topic" /></div>
              <div className="w-44">
                <Label>Niveau</Label>
                <Select value={level} onValueChange={setLevel}>
                  <SelectTrigger data-testid="gen-level"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="débutant">Débutant</SelectItem>
                    <SelectItem value="intermédiaire">Intermédiaire</SelectItem>
                    <SelectItem value="avancé">Avancé</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={generate} disabled={genLoading} className="bg-accent hover:bg-accent/90" data-testid="gen-course-btn">{genLoading ? "Génération..." : "Générer"}</Button>
            </div>
          </div>

          {course && (
            <div className="rounded-xl border border-border bg-card p-6">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="font-display text-2xl font-extrabold">{course.title}</h2>
                  <p className="mt-1 text-sm text-muted-foreground">{course.description}</p>
                </div>
                <Button onClick={saveCourse} disabled={saving} className="bg-primary hover:bg-primary/90" data-testid="save-gen-course-btn"><FloppyDisk size={16} className="mr-2" /> {saving ? "..." : "Créer ce cours"}</Button>
              </div>
              <div className="mt-4 space-y-3">
                {(course.lessons || []).map((l, i) => (
                  <div key={i} className="rounded-lg border border-border p-4">
                    <h3 className="font-display font-bold">Leçon {i + 1} : {l.title}</h3>
                    <p className="mt-1 whitespace-pre-wrap text-sm text-muted-foreground">{l.content}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="summary" className="space-y-4">
          <div className="rounded-xl border border-border bg-card p-6">
            <Label>Texte à résumer</Label>
            <Textarea rows={6} value={sumText} onChange={(e) => setSumText(e.target.value)} placeholder="Collez un contenu de cours..." data-testid="summary-input" />
            <Button onClick={summarize} disabled={sumLoading} className="mt-3 bg-accent hover:bg-accent/90" data-testid="summary-btn">{sumLoading ? "..." : "Résumer"}</Button>
            {summary && <div className="mt-4 whitespace-pre-wrap rounded-lg bg-secondary p-4 text-sm" data-testid="summary-result">{summary}</div>}
          </div>
        </TabsContent>

        <TabsContent value="translate" className="space-y-4">
          <div className="rounded-xl border border-border bg-card p-6">
            <div className="flex items-end gap-3">
              <div className="flex-1"><Label>Texte à traduire</Label></div>
              <div className="w-44">
                <Select value={target} onValueChange={setTarget}>
                  <SelectTrigger data-testid="translate-target"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="anglais">Anglais</SelectItem>
                    <SelectItem value="espagnol">Espagnol</SelectItem>
                    <SelectItem value="français">Français</SelectItem>
                    <SelectItem value="arabe">Arabe</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Textarea rows={6} value={trText} onChange={(e) => setTrText(e.target.value)} className="mt-2" data-testid="translate-input" />
            <Button onClick={translate} disabled={trLoading} className="mt-3 bg-accent hover:bg-accent/90" data-testid="translate-btn">{trLoading ? "..." : "Traduire"}</Button>
            {translation && <div className="mt-4 whitespace-pre-wrap rounded-lg bg-secondary p-4 text-sm" data-testid="translate-result">{translation}</div>}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
